import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output
} from '@angular/core';
import { List as ImmutableList, List } from 'immutable';
import { RoleType } from '../../constants-types';
import {
    Group,
    GroupUpdate,
    GroupValidateResponse,
    IconSize,
    IconType,
    User
} from '../../core-data-model';
import {
    ContextDataService,
    GroupsDataService,
    GroupTransformers,
    notification
} from '../../core-services';
import { Util } from '../../core-utilities';
import {
    InitDataService,
    UserProfileSettingsService
} from '../../system-services';
import { TranslationService } from '../../translations';

type SharedGroupWithUsers = {
    group: Group;
    users: List<User>;
};

@Component({
    selector: 'team-share',
    template: `
        <div class="form formInPeekPanel">
            <div class="title-dashboard" style="margin-bottom: 35px">
                {{ TR.key('sharedTeams.shareThisTeam') }}
            </div>
            <div class="form-group">
                <label class="control-label">
                    @if (sharedGroup) {
                        <gated-pillbox
                            [(ngModel)]="pick"
                            [placeholder]="
                                TR.key(
                                    'useradministration.searchbar-placeholder'
                                )
                            "
                            [checkForThirdPartyUsers]="true"
                            (userAdded)="share($event)"
                            (userRemoved)="unshareGroupFromUser($event)"
                            [sharedUsers]="sharedGroup.users"
                            [busy]="userBeingAdded"
                        >
                        </gated-pillbox>
                    }
                </label>
            </div>
            <div style="margin-top:30px;">
                <div class="title-md" style="margin-bottom:15px">
                    {{
                        TR.key(
                            'label.admin.foldergroupdetails.propertylabel.allowedusers'
                        )
                    }}
                </div>
                @if (hasSharedUsers()) {
                    <div
                        class="layoutHorizontal layoutFixedHorizontalSmallSpacing"
                    >
                        <pi-toggle
                            [(ngModel)]="sharingEnabled"
                            (ngModelChange)="updateSharingEnabled($event)"
                            [disabled]="!thisIsMyTeam()"
                            labelKey="teams.enableAccess"
                            class="strategySurveyResults_toggle--strategy"
                            tooltip
                            [tooltipContent]="
                                TR.key('teams.onlyOwnerCanEnableAccessTooltip')
                            "
                            [tooltipDisabled]="thisIsMyTeam()"
                            [tooltipAnimation]="true"
                            tooltipPlacement="bottom"
                        ></pi-toggle>
                        @if (thisIsMyTeam()) {
                            <span
                                style="display:inline"
                                tooltip
                                [tooltipContent]="
                                    TR.key('teams.enableAccessTooltip')
                                "
                                [tooltipDisabled]="false"
                                [tooltipAnimation]="true"
                                tooltipPlacement="bottom"
                            >
                                <abstract-icon
                                    class="v-align-middle color-primary"
                                    [iconType]="IconType.Info"
                                    [iconSize]="IconSize.Medium"
                                >
                                </abstract-icon>
                            </span>
                        }
                    </div>
                }
            </div>
            <hr class="border-color-neutral-400" />
            @if (!hasSharedUsers()) {
                <div style="margin: 30px 0; font-style: italic;">
                    {{ TR.key('teams.noUsersSharedYet') }}
                </div>
            }
            @if (hasSharedUsers()) {
                <div>
                    @for (user of sharedGroup.users; track user) {
                        <div
                            class="layoutHorizontalSpaceBetween"
                            style="align-items: center;"
                        >
                            <ok-pill
                                [item]="user"
                                [removable]="canBeRemoved(user)"
                                (onRemove)="unshareGroupFromUser(user)"
                            >
                                <ng-template let-item="item">
                                    <span
                                        tooltip
                                        [tooltipContent]="
                                            TR.key(
                                                'teams.cantRemoveSelfTooltip'
                                            )
                                        "
                                        [tooltipDisabled]="canBeRemoved(user)"
                                        [tooltipAnimation]="true"
                                        tooltipPlacement="bottom"
                                    >
                                        {{ displayPill(item) }}
                                    </span>
                                </ng-template>
                            </ok-pill>
                            <span
                                [ngStyle]="{
                                    'font-weight': canEdit(user) ? '500' : '300'
                                }"
                            >
                                {{
                                    TR.key(
                                        canEdit(user)
                                            ? 'teams.canEdit'
                                            : 'teams.canView'
                                    )
                                }}
                            </span>
                        </div>
                    }
                </div>
                <hr class="border-color-neutral-400" />
            }
            <button
                class="button button-tertiary button-md"
                (click)="close.emit()"
                style="margin-top: 40px;"
            >
                {{ TR.key('text.common-terms.close') }}
            </button>
            @if (groupValidationResponse) {
                <shared-groups-permission-modal
                    [validateResponse]="groupValidationResponse"
                    (closeModal)="modalClose()"
                    [isThirdPartyUser]="!isDirectUserOrSuperAdmin()"
                    (addUser)="shareGroupToUserAfterValidation($event)"
                    (removeUser)="removeUserFromPillbox($event)"
                >
                </shared-groups-permission-modal>
            }
        </div>
    `,
    styles: [
        `
            hr {
                border-top-width: 1px;
                margin: 20px 0;
            }
        `
    ],
    standalone: false
})
export class TeamShareComponent implements OnChanges {
    @Input()
    myGroup: Group;
    @Input()
    isCreatingGroup = false;

    @Output()
    close = new EventEmitter();

    @Output()
    groupUpdated = new EventEmitter<GroupUpdate>();

    sharingEnabled: boolean;
    sharedGroup: SharedGroupWithUsers;
    pick = ImmutableList();
    userBeingAdded = false;

    IconType = IconType;
    IconSize = IconSize;

    groupValidationResponse: GroupValidateResponse;

    // Only direct users and super admins can share groups with third party users and can grant folder permissions to users.
    isDirectUserOrSuperAdmin() {
        return (
            this.userProfileService.isUserLoggedInDefaultCompany() ||
            this.init.hasPermissionLevelOrAbove(RoleType.SuperAdmin)
        );
    }

    constructor(
        public TR: TranslationService,
        public util: Util,
        public context: ContextDataService,
        public groups: GroupsDataService,
        private init: InitDataService,
        private userProfileService: UserProfileSettingsService,
        private transform: GroupTransformers
    ) {}

    // Note this one should not be changed when updating edit access to others
    thisIsMyTeam() {
        return this.groups.isGroupOwner(this.myGroup);
    }

    hasSharedUsers() {
        return this.sharedGroup?.users && this.sharedGroup.users.size > 0;
    }

    canEdit(user: User) {
        const userHasAccess = user.role !== RoleType.ReadOnly;
        return this.sharingEnabled && userHasAccess;
    }

    canBeRemoved(user: User) {
        return user.id !== this.init.user.userGuid;
    }

    displayPill(user: User) {
        let roleTag = '';
        if (user.role === RoleType.ReadOnly) {
            roleTag = `(${this.TR.key('text.role.readonly')})`;
        }
        return `${user.getFullName()} ${roleTag}`;
    }

    async updateSharingEnabled(sharing: boolean) {
        if (this.myGroup.isEditableBySharedUsers !== sharing) {
            const groupUpdate = new GroupUpdate(this.myGroup, {
                isEditableBySharedUsers: sharing
            });
            this.groupUpdated.emit(groupUpdate);
        }
    }

    ngOnChanges() {
        if (this.myGroup) {
            this.sharingEnabled = this.myGroup.isEditableBySharedUsers;
            this.getSharedUsers();
        }
    }

    async getSharedUsers() {
        const allUsers = await this.groups.getUsersWithGroupAccess(
            this.myGroup.id
        );
        this.sharedGroup = {
            group: this.myGroup,
            users: ImmutableList(allUsers)
        };
    }

    async share(user: User) {
        this.userBeingAdded = true;
        try {
            await this.validateUserAndShareGroup(user);
            this.pick = ImmutableList();
        } finally {
            this.userBeingAdded = false;
        }
    }

    async validateUserAndShareGroup(user: User) {
        const validate = await this.groups.validateGroups(
            user.id,
            this.sharedGroup.group.id
        );

        if (validate.length > 0) {
            this.groupValidationResponse = validate[0];
        } else {
            await this.shareGroupToUserNoFolders(
                this.sharedGroup.group.id,
                user.id
            );
        }
    }

    @notification('sharedgroups.success-title', 'sharedteams.success-message')
    async shareGroupToUserNoFolders(groupId: string, userId: string) {
        await this.groups.giveUsersAccessToGroup({
            sharedGroupsUsersFoldersParameters: [
                {
                    userCompanyMyGroupGuid: groupId,
                    userGuid: userId
                }
            ]
        });
        await this.getSharedUsers();
    }

    @notification('sharedgroups.success-title', 'sharedteams.success-message')
    async shareGroupToUserAfterValidation(validation: GroupValidateResponse) {
        const shareGroupData =
            this.transform.transformUserAccessClientToServer(validation);

        await this.groups.giveUsersAccessToGroup(shareGroupData);
        await this.getSharedUsers();
    }

    @notification(
        'sharedGroups.success-title',
        'sharedteams.remove-success-message'
    )
    async unshareGroupFromUser(user: User) {
        const unshareGroupData =
            this.transform.transformUnshareUserClientToServer(
                user,
                this.sharedGroup.group.id
            );

        await this.groups.revokeUsersAccessToGroup(unshareGroupData);
        await this.getSharedUsers();
    }

    removeUserFromPillbox(userId: string) {
        const index = this.sharedGroup.users.findIndex((u) => u.id === userId);

        if (index !== -1) {
            this.sharedGroup.users = this.sharedGroup.users.delete(index);
        }
    }

    modalClose() {
        this.groupValidationResponse = null;
    }
}
